import React from 'react';
const { useState, useEffect, useMemo } = React;
import { adminCall } from '../diagnostic/shared.jsx';
import { coachCall } from '../coaching/shared.jsx';
import { DESTINATIONS, reviewOf, defaultDiagnosticName, defaultProcessName } from '../../serviceStart.js';
import { ROLES } from '../../diagnosticConfig.js';
import { ErrorState, LoadingState } from '../ui/States.jsx';

// "Preparar inicio del servicio" (docs/servicios/inicio.md): from an accepted proposal,
// choose a line, choose where it starts (Diagnostic, the System Design of a diagnostic,
// or Acompañamiento with a program), reuse the people's cards, review, and create or
// link. The servers decide and check everything again; this panel only asks.

const muted = { fontSize: 12, color: 'var(--globant-muted)', lineHeight: 1.5 };
const label = { fontSize: 11, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 0.6 };
const box = { border: '1px solid var(--globant-border)', borderRadius: 10, padding: 12, display: 'grid', gap: 8 };
const money = (v, c) => (v === '' || v === null || v === undefined ? '' : `${c || 'USD'} ${Number(v).toLocaleString('es')}`);
const ROLE_OPTIONS = Object.entries(ROLES).map(([k, r]) => ({ key: k, label: r.label?.es || k }));

/** Where to open what was prepared: the diagnostic in its company, the process in Acompañamiento. */
export function openPrepared(kind, { id, accountId }) {
  if (kind === 'coaching') { window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'coaching', processId: id } })); return; }
  window.__oikePendingDiagnostic = id;
  window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'accounts', accountId, tab: 'diagnostic', diagnosticId: id } }));
}

export default function ServiceStartPanel({ quote, accountName, facts, onClose }) {
  const [diag, setDiag] = useState(null);      // diagnostic-admin options, or { error }
  const [coach, setCoach] = useState(null);    // coaching-admin options, or { error }
  const [lineKey, setLineKey] = useState('');
  const [plan, setPlan] = useState(null);      // the choices for the selected line
  const [step, setStep] = useState('choose');  // choose → review → done
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [matches, setMatches] = useState(null); // person_exists: { stakeholderId, matches }
  const [done, setDone] = useState(null);

  const load = () => {
    setDiag(null); setCoach(null);
    adminCall({ action: 'serviceStartDiagnostic', quoteId: quote.id }).then(setDiag).catch(e => setDiag({ error: e.message, code: e.code }));
    coachCall({ action: 'serviceStartCoaching', quoteId: quote.id }).then(setCoach).catch(e => setCoach({ error: e.message, code: e.code }));
  };
  useEffect(load, [quote.id]);

  const base = diag && !diag.error ? diag : coach && !coach.error ? coach : null;
  const lines = base?.quote?.lines || [];
  const accountId = base?.quote?.accountId || '';
  const contacts = (diag && !diag.error ? diag.contacts : coach?.contacts) || [];
  const currency = quote.fields?.['Quote Currency'] || 'USD';
  const originsMissing = (diag && !diag.error && diag.originsReady === false) || (coach && !coach.error && coach.originsReady === false);

  // What already holds each line (one line, one process)
  const holders = useMemo(() => {
    const out = {};
    for (const d of (diag && !diag.error ? diag.diagnostics : []) || []) for (const o of d.origins || []) if (o.quoteId === quote.id) out[o.lineKey] = { kind: 'diagnostic', id: d.id, name: d.name, destination: o.destination, mode: o.mode, at: o.at };
    for (const p of (coach && !coach.error ? coach.processes : []) || []) for (const o of p.origins || []) if (o.quoteId === quote.id) out[o.lineKey] = { kind: 'coaching', id: p.id, name: p.name, destination: 'coaching', mode: o.mode, at: o.at };
    return out;
  }, [diag, coach, quote.id]);

  const line = lines.find(l => l.lineKey === lineKey) || null;

  const choose = (l) => {
    setLineKey(l.lineKey); setStep('choose'); setError(''); setMatches(null); setDone(null);
    const sourceDiagnostic = base?.quote?.sourceDiagnosticId || '';
    const holderOfDiagnosticLine = Object.values(holders).find(h => h.destination === 'diagnostic')?.id || '';
    setPlan({
      destination: l.suggested, mode: 'create',
      diagnosticId: l.suggested === 'systemDesign' ? (sourceDiagnostic || holderOfDiagnosticLine) : '',
      name: '', language: 'es', programId: '', processId: '', sponsorName: '', sponsorEmail: '',
      people: {}, // stakeholderId → { on, role, personId, confirmNew }
    });
  };

  const program = (coach?.programs || []).find(p => p.id === plan?.programId) || null;
  const chosenPeople = Object.entries(plan?.people || {}).filter(([, v]) => v.on).map(([stakeholderId, v]) => ({ stakeholderId, ...v }));
  const contactOf = (id) => contacts.find(c => c.contact.id === id);
  const personLabel = (c, v) => {
    if (c.person) return { name: c.person.name, card: 'existing' };
    if (v?.personId) return { name: (c.suggestions || []).find(s => s.id === v.personId)?.name || c.contact.name, card: 'existing' };
    return { name: c.contact.name, card: 'from-contact' };
  };

  // What gets created or linked, and the default names
  const target = useMemo(() => {
    if (!plan || !line) return null;
    if (plan.destination === 'diagnostic') {
      if (plan.mode === 'link') return (diag?.diagnostics || []).find(d => d.id === plan.diagnosticId) || null;
      return { name: plan.name || defaultDiagnosticName(accountName) };
    }
    if (plan.destination === 'systemDesign') return (diag?.diagnostics || []).find(d => d.id === plan.diagnosticId) || null;
    if (plan.destination === 'coaching') {
      if (plan.mode === 'link') return (coach?.processes || []).find(p => p.id === plan.processId) || null;
      const first = chosenPeople[0] && personLabel(contactOf(chosenPeople[0].stakeholderId), chosenPeople[0]);
      return program ? { name: plan.name || defaultProcessName({ modality: program.modality, personName: first?.name, programName: program.name, accountName }) } : null;
    }
    return null;
  }, [plan, line, diag, coach, program, chosenPeople.length]);

  const problems = useMemo(() => {
    if (!plan || !line) return [];
    const p = [];
    if (plan.destination === 'none') p.push('Esta línea no tiene recorrido en Oike: no se crea nada.');
    if (originsMissing) p.push('Faltan los campos de origen en Airtable: se puede revisar, pero no crear ni vincular.');
    if (plan.destination === 'diagnostic' && plan.mode === 'link' && !target) p.push('Elegí el diagnóstico a vincular.');
    if (plan.destination === 'systemDesign' && !target) p.push('Elegí el diagnóstico donde va el System Design (si no hay, prepará primero el Diagnostic).');
    if (plan.destination === 'coaching') {
      if (coach?.error) p.push(`Acompañamiento: ${coach.error}`);
      if (plan.mode === 'link' && !target) p.push('Elegí el proceso a vincular.');
      if (plan.mode !== 'link') {
        if (!program) p.push('Elegí el programa.');
        if (program?.modality === 'individual' && chosenPeople.length !== 1) p.push('Un proceso individual lleva una sola persona.');
        if (program?.modality === 'team' && chosenPeople.length < 2) p.push('Un proceso de equipo lleva al menos dos personas.');
      }
    }
    if ((plan.destination === 'diagnostic' || plan.destination === 'systemDesign') && diag?.error) p.push(`Diagnostic: ${diag.error}`);
    // A contact with cards of the same email: Ale says which one (or that it is another person); never merged alone
    if (plan.mode !== 'link' && plan.destination !== 'systemDesign') for (const v of chosenPeople) {
      const c = contactOf(v.stakeholderId);
      if (c && !c.person && c.suggestions?.length && !v.personId && !v.confirmNew) p.push(`Elegí la ficha de ${c.contact.name}: hay fichas con su email.`);
    }
    return p;
  }, [plan, line, target, program, chosenPeople.length, originsMissing, diag, coach, contacts]);

  const reviewLines = plan && line && target ? reviewOf({
    destination: plan.destination, mode: plan.destination === 'systemDesign' || plan.mode === 'link' ? 'linked' : 'created',
    targetName: target.name, accountName, programName: program?.name, quoteName: quote.fields?.Name || base?.quote?.name, service: line.service,
    people: plan.mode === 'link' || plan.destination === 'systemDesign' ? [] : chosenPeople.map(v => personLabel(contactOf(v.stakeholderId), v)),
  }) : [];

  const save = async () => {
    if (saving) return; // a second click while saving does nothing (and the server would return the same process)
    setSaving(true); setError(''); setMatches(null);
    try {
      const people = chosenPeople.map(v => ({ stakeholderId: v.stakeholderId, ...(v.personId ? { personId: v.personId } : {}), ...(v.confirmNew ? { confirmNew: true } : {}), role: v.role || 'founder_seller' }));
      if (plan.destination === 'coaching') {
        const r = await coachCall({ action: 'prepareProcess', quoteId: quote.id, lineKey: line.lineKey, mode: plan.mode, processId: plan.processId,
          programId: plan.programId, name: target?.name, people, sponsorName: plan.sponsorName, sponsorEmail: plan.sponsorEmail });
        setDone({ kind: 'coaching', id: r.processId, already: r.already, name: target?.name });
      } else {
        const r = await adminCall({ action: 'prepareDiagnostic', quoteId: quote.id, lineKey: line.lineKey, destination: plan.destination,
          mode: plan.mode, diagnosticId: plan.diagnosticId, name: target?.name, language: plan.language, participants: plan.destination === 'diagnostic' && plan.mode !== 'link' ? people : [] });
        setDone({ kind: 'diagnostic', id: r.diagnostic.id, already: r.already, name: r.diagnostic.name, failed: r.failed || [] });
      }
      setStep('done');
      load();
    } catch (e) {
      if (e.code === 'person_exists') setMatches(e.data);
      setError(e.message);
    }
    setSaving(false);
  };

  const setPerson = (id, patch) => setPlan(p => ({ ...p, people: { ...p.people, [id]: { ...(p.people[id] || {}), ...patch } } }));

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <div className="modal" role="dialog" aria-label="Preparar inicio del servicio" onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 760, maxHeight: '90vh', overflowY: 'auto', display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <div>
            <div style={label}>Preparar inicio del servicio</div>
            <h3 style={{ margin: '2px 0 0' }}>{quote.fields?.Name || 'Propuesta'}</h3>
            <div style={muted}>{accountName || 'Sin empresa'}</div>
          </div>
          <button type="button" className="action-btn btn-ghost" onClick={onClose}>Cerrar</button>
        </div>

        <div style={{ ...box, background: 'rgba(154,98,18,0.06)', borderColor: 'rgba(154,98,18,0.25)' }}>
          <div style={{ fontSize: 13 }}><b>Propuesta aceptada.</b> No significa pago recibido ni servicio iniciado: preparar o crear el proceso tampoco lo marca.</div>
          {facts && <div style={muted}>Pago: {facts.paymentReceived?.state === 'confirmed' ? 'registrado' : 'pendiente de confirmar'} · Inicio del servicio: {facts.serviceStarted?.state === 'confirmed' ? 'registrado' : 'pendiente de confirmar'} (se registran en el pipeline)</div>}
        </div>

        {originsMissing && <ErrorState message="Faltan los campos de origen en Airtable (docs/servicios/inicio.md). Podés revisar el recorrido, pero no se crea ni se vincula nada hasta que Omni los agregue." />}
        {!diag || !coach ? <LoadingState text="Cargando la propuesta, las personas y los procesos…" /> : !base ? <ErrorState message={diag.error || coach.error} onRetry={load} /> : (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={label}>Servicios de la propuesta</div>
            {lines.map(l => {
              const h = holders[l.lineKey];
              return (
                <div key={l.lineKey} style={{ ...box, borderColor: lineKey === l.lineKey ? 'var(--globant-green)' : 'var(--globant-border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
                    <b style={{ fontSize: 14 }}>{l.service}</b>
                    <span style={muted}>{[money(l.onceFee, currency), l.monthlyFee ? `${money(l.monthlyFee, currency)}/mes` : ''].filter(Boolean).join(' · ')}</span>
                  </div>
                  {l.scope && <div style={muted}>{l.scope}</div>}
                  {h ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#1E7B4F' }}>✓ {h.mode === 'linked' ? 'Vinculado' : 'Preparado'}: {DESTINATIONS[h.destination].label} «{h.name}»</span>
                      <button type="button" className="action-btn" style={{ fontSize: 11 }} onClick={() => { openPrepared(h.kind, { id: h.id, accountId }); onClose(); }}>Abrir</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={muted}>{l.suggested === 'none' ? (l.missingRoute?.missing || DESTINATIONS.none.label) : `Se inicia en ${DESTINATIONS[l.suggested].label}.`}</span>
                      <button type="button" className="action-btn btn-primary" style={{ fontSize: 11 }} onClick={() => choose(l)}>{lineKey === l.lineKey ? 'Elegido' : 'Elegir este servicio'}</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {line && plan && step !== 'done' && (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={label}>{step === 'review' ? 'Revisar antes de crear' : `Configurar: ${line.service}`}</div>
            {step === 'choose' && (
              <>
                <label style={muted}>Dónde se inicia
                  <select className="input-field" style={{ width: '100%' }} value={plan.destination} onChange={e => setPlan(p => ({ ...p, destination: e.target.value, mode: 'create' }))}>
                    {line.destinations.map(d => <option key={d} value={d}>{DESTINATIONS[d].label}{d === line.suggested ? '' : ' (elegido por vos)'}</option>)}
                  </select>
                </label>

                {plan.destination === 'none' && line.missingRoute && (
                  <div style={box}><div style={{ fontSize: 13 }}>{line.missingRoute.missing}</div><div style={muted}>{line.missingRoute.minimal}</div></div>
                )}

                {plan.destination === 'diagnostic' && (
                  <div style={box}>
                    <ModeChoice value={plan.mode} onChange={mode => setPlan(p => ({ ...p, mode }))} createLabel="Crear un diagnóstico nuevo" linkLabel="Vincular un diagnóstico existente de la empresa"
                      canLink={(diag?.diagnostics || []).some(d => !d.origins.some(o => o.destination === 'diagnostic'))} />
                    {plan.mode === 'link' ? (
                      <select className="input-field" value={plan.diagnosticId} onChange={e => setPlan(p => ({ ...p, diagnosticId: e.target.value }))}>
                        <option value="">Elegí el diagnóstico…</option>
                        {(diag?.diagnostics || []).filter(d => !d.origins.some(o => o.destination === 'diagnostic')).map(d => <option key={d.id} value={d.id}>{d.name}{d.isTest ? ' (prueba)' : ''} · {d.status}</option>)}
                      </select>
                    ) : (
                      <>
                        <label style={muted}>Nombre<input className="input-field" style={{ width: '100%' }} value={plan.name} placeholder={defaultDiagnosticName(accountName)} maxLength={200} onChange={e => setPlan(p => ({ ...p, name: e.target.value }))} /></label>
                        <PeoplePicker contacts={contacts} people={plan.people} setPerson={setPerson} withRole title="Participantes (opcional: los podés sumar después en el Diagnostic)" />
                      </>
                    )}
                  </div>
                )}

                {plan.destination === 'systemDesign' && (
                  <div style={box}>
                    <div style={muted}>System Design no es un proceso aparte: vive dentro de un diagnóstico. Esta línea se vincula al diagnóstico que elijas{base?.quote?.sourceDiagnosticId ? ' (la propuesta salió de uno)' : ''}.</div>
                    <select className="input-field" value={plan.diagnosticId} onChange={e => setPlan(p => ({ ...p, diagnosticId: e.target.value }))}>
                      <option value="">Elegí el diagnóstico…</option>
                      {(diag?.diagnostics || []).filter(d => !d.origins.some(o => o.destination === 'systemDesign')).map(d => <option key={d.id} value={d.id}>{d.name} · {d.status}</option>)}
                    </select>
                  </div>
                )}

                {plan.destination === 'coaching' && (coach?.error ? <ErrorState message={`Acompañamiento: ${coach.error}`} /> : (
                  <div style={box}>
                    <ModeChoice value={plan.mode} onChange={mode => setPlan(p => ({ ...p, mode }))} createLabel="Crear un proceso nuevo desde un programa" linkLabel="Vincular un proceso existente"
                      canLink={(coach?.processes || []).some(p => !p.origins.length && p.status !== 'closed')} />
                    {plan.mode === 'link' ? (
                      <>
                        <select className="input-field" value={plan.processId} onChange={e => setPlan(p => ({ ...p, processId: e.target.value }))}>
                          <option value="">Elegí el proceso…</option>
                          {(coach?.processes || []).filter(p => !p.origins.length && p.status !== 'closed').map(p => <option key={p.id} value={p.id}>{p.name} · {p.modality === 'team' ? 'equipo' : 'individual'} · {p.status}</option>)}
                        </select>
                        <div style={muted}>Sus personas, permisos, sesiones y acceso desde el espacio de cada persona no cambian.</div>
                      </>
                    ) : (
                      <>
                        <label style={muted}>Programa
                          <select className="input-field" style={{ width: '100%' }} value={plan.programId} onChange={e => setPlan(p => ({ ...p, programId: e.target.value }))}>
                            <option value="">Elegí el programa…</option>
                            {(coach?.programs || []).map(p => <option key={p.id} value={p.id}>{p.name} · {p.modality === 'team' ? 'equipo' : 'individual'}</option>)}
                          </select>
                        </label>
                        {program?.description && <div style={muted}>{program.description}</div>}
                        <PeoplePicker contacts={contacts} people={plan.people} setPerson={setPerson} title={program?.modality === 'team' ? 'Personas del equipo' : 'Persona acompañada'} />
                        <label style={muted}>Nombre del proceso<input className="input-field" style={{ width: '100%' }} value={plan.name} placeholder={target?.name || ''} maxLength={200} onChange={e => setPlan(p => ({ ...p, name: e.target.value }))} /></label>
                        {program?.modality === 'team' && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <label style={muted}>Quien contrata (opcional)<input className="input-field" style={{ width: '100%' }} value={plan.sponsorName} maxLength={200} onChange={e => setPlan(p => ({ ...p, sponsorName: e.target.value }))} /></label>
                            <label style={muted}>Su email<input className="input-field" style={{ width: '100%' }} value={plan.sponsorEmail} maxLength={200} onChange={e => setPlan(p => ({ ...p, sponsorEmail: e.target.value }))} /></label>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}

                {problems.length > 0 && <ul style={{ ...muted, margin: 0, paddingLeft: 18 }}>{problems.map(p => <li key={p}>{p}</li>)}</ul>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="action-btn btn-primary" disabled={problems.length > 0} onClick={() => setStep('review')}>Revisar</button>
                </div>
              </>
            )}

            {step === 'review' && (
              <div style={box}>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>{reviewLines.map(t => <li key={t}>{t}</li>)}</ul>
                {matches && (
                  <div style={{ ...box, borderColor: 'rgba(154,98,18,0.4)' }}>
                    <div style={{ fontSize: 13 }}>Ya hay fichas con ese email. Elegí si es la misma persona o confirmá que es otra (nunca se unen solas):</div>
                    {(matches.matches || []).map(m => <div key={m.id} style={muted}>{m.name}{m.email ? ` · ${m.email}` : ''}</div>)}
                    <div style={muted}>Volvé a "Configurar" y elegí la ficha en la persona correspondiente.</div>
                  </div>
                )}
                {error && <div role="alert" style={{ fontSize: 12, color: '#B4362C' }}>{error}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="action-btn" disabled={saving} onClick={() => setStep('choose')}>Volver a configurar</button>
                  <button type="button" className="action-btn btn-primary" disabled={saving || originsMissing} onClick={save}>
                    {saving ? 'Guardando…' : plan.destination === 'systemDesign' || plan.mode === 'link' ? 'Vincular' : 'Crear y vincular'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'done' && done && (
          <div style={{ ...box, borderColor: 'rgba(30,123,79,0.4)' }}>
            <div style={{ fontSize: 13 }}>{done.already ? 'Esta línea ya estaba preparada: no se creó nada nuevo.' : 'Listo.'} {done.kind === 'coaching' ? 'Proceso' : 'Diagnóstico'} «{done.name}».</div>
            {done.failed?.length > 0 && <div style={{ fontSize: 12, color: '#B4362C' }}>No se pudieron sumar {done.failed.length} participante(s): sumalos desde el Diagnostic.</div>}
            <div style={muted}>El servicio no figura como iniciado: cuando empiece, registralo en el pipeline.</div>
            <div><button type="button" className="action-btn btn-primary" onClick={() => { openPrepared(done.kind, { id: done.id, accountId }); onClose(); }}>Abrir {done.kind === 'coaching' ? 'el proceso' : 'el diagnóstico'}</button></div>
          </div>
        )}
      </div>
    </div>
  );
}

function ModeChoice({ value, onChange, createLabel, linkLabel, canLink }) {
  return (
    <div style={{ display: 'grid', gap: 4, fontSize: 13 }}>
      <label><input type="radio" checked={value !== 'link'} onChange={() => onChange('create')} /> {createLabel}</label>
      <label style={{ opacity: canLink ? 1 : 0.55 }}><input type="radio" disabled={!canLink} checked={value === 'link'} onChange={() => onChange('link')} /> {linkLabel}{canLink ? '' : ' (no hay ninguno disponible)'}</label>
    </div>
  );
}

/** The contacts of the proposal and its company, each with its card: existing, one with the same email to pick, or new from the contact. */
function PeoplePicker({ contacts, people, setPerson, title, withRole = false }) {
  if (!contacts.length) return <div style={muted}>La propuesta y su empresa no tienen contactos todavía.</div>;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={muted}>{title}</div>
      {contacts.map(c => {
        const v = people[c.contact.id] || {};
        return (
          <div key={c.contact.id} style={{ display: 'grid', gap: 4, padding: '6px 8px', border: '1px solid var(--globant-border)', borderRadius: 8 }}>
            <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="checkbox" checked={!!v.on} onChange={e => setPerson(c.contact.id, { on: e.target.checked })} />
              <b>{c.contact.name}</b><span style={muted}>{[c.contact.role, c.contact.email].filter(Boolean).join(' · ')}</span>
            </label>
            {v.on && (
              <div style={{ ...muted, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {c.person ? <span>Ficha existente: {c.person.name}</span>
                  : c.suggestions?.length ? (
                    <select className="input-field" value={v.personId || (v.confirmNew ? '_new' : '')} onChange={e => setPerson(c.contact.id, e.target.value === '_new' ? { personId: '', confirmNew: true } : { personId: e.target.value, confirmNew: false })}>
                      <option value="">Hay fichas con su email: elegí…</option>
                      {c.suggestions.map(s => <option key={s.id} value={s.id}>Es {s.name}{s.email ? ` (${s.email})` : ''}</option>)}
                      <option value="_new">Es otra persona: crear una ficha nueva</option>
                    </select>
                  ) : <span>Se crea su ficha desde el contacto (nombre y email; nada más)</span>}
                {withRole && (
                  <select className="input-field" value={v.role || 'founder_seller'} onChange={e => setPerson(c.contact.id, { role: e.target.value })}>
                    {ROLE_OPTIONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
